/**
 * In-app user guide ("page guides") — the data contract.
 *
 * Pure data and pure functions: no React, no router, no jotai, no i18next.
 * Guides (`pages/<domain>/help.ts`), the registry, the Node check script and
 * the tests all import this file, so it must stay dependency-free.
 */

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

/**
 * QTG roles are not a linear ladder (a read-only admin may open Settings but
 * not edit offers; an internal user may edit offers but not open Settings), so
 * a step is gated by the same permission the UI uses to hide the control,
 * mirroring `RolePermissions` in `library/constants/roles.ts`.
 */
export type HelpPermission =
  | "canEdit"
  | "canDelete"
  | "canViewSettings"
  | "canEditSettings"
  | "canInvalidateOffers"
  | "canMarkOffersAsError"
  | "canChooseOffersShowMode";

// EDIT: keep in sync with RolePermissions (asserted in registry.test.ts).
export const HELP_PERMISSION_ROLES: Record<HelpPermission, readonly string[]> = {
  canEdit: ["QTG-LV-Admins", "QTG-LV-Internal"],
  canDelete: ["QTG-LV-Admins"],
  canViewSettings: ["QTG-LV-Admins", "QTG-LV-ReadOnly-Admins"],
  canEditSettings: ["QTG-LV-Admins"],
  canInvalidateOffers: ["QTG-LV-Admins", "QTG-LV-Internal"],
  canMarkOffersAsError: ["QTG-LV-Admins"],
  canChooseOffersShowMode: ["QTG-LV-Admins", "QTG-LV-ReadOnly-Admins"],
};

/** What the guide knows about the viewer — plain data, not the jotai store. */
export interface HelpViewer {
  roles: readonly string[];
}

export function viewerHas(viewer: HelpViewer, permission: HelpPermission): boolean {
  const allowed = HELP_PERMISSION_ROLES[permission];
  return viewer.roles.some((role) => allowed.includes(role));
}

export function stepVisibleForViewer(step: HelpStep, viewer: HelpViewer): boolean {
  return !step.requires || viewerHas(viewer, step.requires);
}

// ---------------------------------------------------------------------------
// Languages
// ---------------------------------------------------------------------------

// EDIT: EN is first and always the base text.
export const HELP_LANGUAGES = ["en", "lv"] as const;
export type HelpLanguage = (typeof HELP_LANGUAGES)[number];
export type HelpTranslation = Exclude<HelpLanguage, "en">;

/** Maps an i18next language code ("lv-LV", "en-GB") to a guide language. */
export function helpLanguageFor(code: string | undefined): HelpLanguage {
  const short = (code || "en").slice(0, 2).toLowerCase();
  return (HELP_LANGUAGES as readonly string[]).includes(short)
    ? (short as HelpLanguage)
    : "en";
}

// ---------------------------------------------------------------------------
// The contract
// ---------------------------------------------------------------------------

export interface HelpStepText {
  title?: string;
  body?: string;
  section?: string;
}

export type HelpStepI18n = Partial<Record<HelpTranslation, HelpStepText>>;

export interface HelpStep {
  /** Always the literal `[data-help="…"]` — never a class or a text match. */
  selector: string;
  /** Action phrase: "Filter the list by product". */
  title: string;
  /** 1–4 plain sentences: what it does, when to use it, what happens next. */
  body: string;
  /** Groups the card; order comes from PageHelp.sections, unlisted → shown last. */
  section?: string;
  /** Hide from viewers without this permission — ONLY when the UI hides the control too. */
  requires?: HelpPermission;
  /** Exists only after a UI action — key into PageHelp.contexts. */
  context?: string;
  /** Needs data to exist (a non-empty list, an issued invoice); the audit skips it. */
  optional?: boolean;
  i18n?: HelpStepI18n;
}

export interface PageHelpText {
  title?: string;
  intro?: string;
  tips?: string[];
  /** Keyed by the EN section name. */
  sections?: Record<string, string>;
  /** Keyed by the context key. */
  contexts?: Record<string, string>;
}

export type PageHelpI18n = Partial<Record<HelpTranslation, PageHelpText>>;

export type AskOfferAction = "open" | "menu" | "select";

/** An existing offer the guide brings on screen (ask/journey.ts). */
export interface AskOffer {
  /** OfferStatus codes the offer must be in ("OfferStatus.INVOICE"); omit for any status. */
  statuses?: readonly string[];
  /** Only offers of this product (an offer with both counts for both). */
  product?: "MTPL" | "CASCO";
  /** Open the offer page, open the row's ⋮ menu, or tick the row(s). */
  action: AskOfferAction;
  /** Rows to tick for "select" (default 1). */
  count?: number;
}

/** OfferStatus codes, grouped the way the guide's answers need them. */
export const OFFER_STATUSES = [
  "OfferStatus.OFFER",
  "OfferStatus.OFFER_FROM_PREFILL",
  "OfferStatus.OFFER_SAVED",
  "OfferStatus.OFFER_SENT",
  "OfferStatus.OFFER_UPDATED",
] as const;
export const UNPAID_INVOICE_STATUSES = [
  "OfferStatus.INVOICE",
  "OfferStatus.INVOICE_SENT",
  "OfferStatus.GROUP_INVOICE",
  "OfferStatus.GROUP_INVOICE_SENT",
] as const;
export const PAID_STATUSES = ["OfferStatus.INVOICE_PAID", "OfferStatus.GROUP_INVOICE_PAID"] as const;

export interface HelpRevealJourney {
  /** Selectors to press, as for a plain recipe. */
  press: string[];
  /** When this screen cannot show it: bring up an existing offer that can (see ask/journey.ts)… */
  offer?: AskOffer;
  /** …or start a new offer of this product (through the offers list, as a person would). */
  newOffer?: "mtpl" | "casco";
}

export type HelpReveal = string[] | HelpRevealJourney;

export interface PageHelp {
  title: string;
  intro: string;
  sections?: string[];
  /** How to make a context step appear: "Click New Proposal → New MTPL Proposal." */
  contexts?: Record<string, string>;
  /**
   * How the guide makes a context step appear by itself: selectors pressed in order (first enabled match),
   * stopping as soon as the step is on screen — first on the current screen, then, when the recipe says so, on a
   * suitable other offer or route. Contexts without one (or whose recipe fails) show the text above.
   */
  reveal?: Record<string, HelpReveal>;
  tips?: string[];
  steps: HelpStep[];
  i18n?: PageHelpI18n;
}

// ---------------------------------------------------------------------------
// Localisation — per field, EN fallback, and say when it fell back
// ---------------------------------------------------------------------------

export interface Localized<T> {
  value: T;
  /** false when ANY displayed field fell back to EN. */
  translated: boolean;
}

export function localizedStep(
  step: HelpStep,
  language: HelpLanguage
): Localized<{ title: string; body: string; section?: string }> {
  if (language === "en") {
    return {
      value: { title: step.title, body: step.body, section: step.section },
      translated: true,
    };
  }
  const t = step.i18n?.[language];
  const title = t?.title ?? step.title;
  const body = t?.body ?? step.body;
  const section = step.section && (t?.section ?? step.section);
  // the section heading is translated once per page (PageHelp.i18n.sections), not per step
  const translated = !!t?.title && !!t?.body;
  return { value: { title, body, section }, translated };
}

export function localizedPage(
  page: PageHelp,
  language: HelpLanguage
): Localized<{ title: string; intro: string; tips: string[] }> {
  const tips = page.tips ?? [];
  if (language === "en") {
    return { value: { title: page.title, intro: page.intro, tips }, translated: true };
  }
  const t = page.i18n?.[language];
  const translatedTips = t?.tips && t.tips.length === tips.length ? t.tips : undefined;
  return {
    value: {
      title: t?.title ?? page.title,
      intro: t?.intro ?? page.intro,
      tips: translatedTips ?? tips,
    },
    translated: !!t?.title && !!t?.intro && (tips.length === 0 || !!translatedTips),
  };
}

export function localizedSection(
  page: PageHelp,
  section: string,
  language: HelpLanguage
): Localized<string> {
  if (language === "en") return { value: section, translated: true };
  const t = page.i18n?.[language]?.sections?.[section];
  return { value: t ?? section, translated: !!t };
}

export function localizedContext(
  page: PageHelp,
  key: string,
  language: HelpLanguage
): Localized<string> | null {
  const en = page.contexts?.[key];
  if (en === undefined) return null;
  if (language === "en") return { value: en, translated: true };
  const t = page.i18n?.[language]?.contexts?.[key];
  return { value: t ?? en, translated: !!t };
}

// ---------------------------------------------------------------------------
// Grouping, search, reverse lookup
// ---------------------------------------------------------------------------

/** Name used for steps without a declared section. */
export const MORE_SECTION = "More";

export interface StepGroup {
  section: string;
  steps: HelpStep[];
}

/**
 * Declared order first; sections used but not declared follow in first-seen
 * order; steps without a section go to a trailing "More". Empty groups are
 * dropped. Every step lands in exactly one group.
 */
export function groupStepsBySection(
  steps: readonly HelpStep[],
  declared: readonly string[] = []
): StepGroup[] {
  const order: string[] = [...declared];
  const buckets = new Map<string, HelpStep[]>();
  for (const name of declared) buckets.set(name, []);
  for (const step of steps) {
    const name = step.section ?? MORE_SECTION;
    if (!buckets.has(name)) {
      buckets.set(name, []);
      if (name !== MORE_SECTION) order.push(name);
    }
    buckets.get(name)!.push(step);
  }
  if (buckets.has(MORE_SECTION) && !order.includes(MORE_SECTION)) order.push(MORE_SECTION);
  return order
    .map((section) => ({ section, steps: buckets.get(section) ?? [] }))
    .filter((group) => group.steps.length > 0);
}

const SELECTOR_RE = /^\[data-help="([a-z0-9-]+)"\]$/;

/** `[data-help="offers-search"]` → `offers-search`; anything else → null. */
export function anchorNameOf(selector: string): string | null {
  return SELECTOR_RE.exec(selector)?.[1] ?? null;
}

export const HELP_SELECTOR_RE = SELECTOR_RE;

/**
 * Every whitespace-separated term must appear, in any order, in the title,
 * body, section, selector or any translation. Bodies are searched on purpose
 * (the user who cannot name a field is the one searching) and the match is
 * language-blind on purpose (an untranslated step must not vanish).
 */
export function stepMatchesQuery(step: HelpStep, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const parts: string[] = [step.title, step.body, step.section ?? "", step.selector];
  for (const t of Object.values(step.i18n ?? {})) {
    if (t) parts.push(t.title ?? "", t.body ?? "", t.section ?? "");
  }
  const haystack = parts.join("\n").toLowerCase();
  return terms.every((term) => haystack.includes(term));
}
